import { useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
} from "recharts";
import { Info, TrendingDown, Users } from "lucide-react";

// Fixed volume-based pricing model (CNP Managed Services). Pre-MSP baseline = $130/hour.
const BASELINE = 130;
const TIERS = [
  { key: "current", sizeLabel: "18", minSize: 0, name: "Current with MSP", rate: 124, chartLabel: "5%", chartPct: 5 },
  { key: "pipeline", sizeLabel: "23", minSize: 23, name: "Pipeline", rate: 113, chartLabel: "9%", chartPct: 9 },
  { key: "target", sizeLabel: "40+", minSize: 40, name: "Target", rate: 104, chartLabel: "20%", chartPct: 20 },
];

// Exact savings vs the pre-MSP $130/hour baseline (cumulative).
const exactSavings = (rate) => Math.round(((BASELINE - rate) / BASELINE) * 1000) / 10;
// Exact step savings vs the previous tier's rate.
const stepSavings = (rate, prevRate) => Math.round(((prevRate - rate) / prevRate) * 1000) / 10;

const tierForSize = (size) => (size >= 40 ? TIERS[2] : size >= 23 ? TIERS[1] : TIERS[0]);

const chartData = TIERS.map((t) => ({
  tier: `${t.sizeLabel} · ${t.name}`, pct: t.chartPct, label: t.chartLabel,
}));

export default function ManagedServicesValue() {
  const [teamSize, setTeamSize] = useState(18);
  const active = tierForSize(teamSize);
  const activeSavings = exactSavings(active.rate);

  return (
    <div className="space-y-5 fluent-enter" data-testid="msp-value-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#17365D]">Managed Services — Value &amp; Savings</h1>
        <p className="text-sm text-[#605E5C] mt-1">CNP value delivered through volume-based pricing. Blended cost per hour falls as the team scales.</p>
      </div>

      {/* Scaling roadmap */}
      <div className="fluent-card p-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm" data-testid="scaling-roadmap">
        <span className="inline-flex items-center gap-2 font-semibold text-[#201F1E]"><Users size={16} className="text-[#0078D4]" /> Team scaling road-map</span>
        <span className="text-[#323130]">Current team size = <b>18</b></span>
        <span className="text-[#323130]">Expected until end of October = <b>23</b></span>
        <span className="text-[#323130]">Pre-MSP baseline = <b>${BASELINE}/hour</b></span>
      </div>

      {/* Interactive: blended rate updates with team size */}
      <div className="fluent-card p-5" data-testid="blended-calculator">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">Team size</div>
            <div className="flex items-center gap-3 mt-1">
              <input
                type="range" min={10} max={60} step={1} value={teamSize}
                onChange={(e) => setTeamSize(Number(e.target.value))}
                data-testid="team-size-slider" className="w-56 accent-[#0078D4]"
              />
              <span className="text-lg font-semibold text-[#201F1E] w-10" data-testid="team-size-value">{teamSize}</span>
            </div>
            <div className="mt-1 text-xs text-[#605E5C]">
              Active tier: <b data-testid="active-tier-name">{active.name}</b> ({active.sizeLabel} members)
            </div>
          </div>
          <div className="flex items-stretch gap-3">
            <div className="rounded-lg border border-[#E1DFDD] px-5 py-3 text-center min-w-[140px]">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">Blended rate</div>
              <div className="text-3xl font-semibold text-[#17365D]" data-testid="blended-rate-value">${active.rate}</div>
              <div className="text-[11px] text-[#605E5C]">per hour</div>
            </div>
            <div className="rounded-lg border px-5 py-3 text-center min-w-[140px]" style={{ background: "#E8F5E9", borderColor: "#107C41" }}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#107C41]">Savings vs ${BASELINE}</div>
              <div className="text-3xl font-semibold text-[#107C41]" data-testid="active-savings-value">{activeSavings}%</div>
              <div className="text-[11px] text-[#107C41]">${BASELINE - active.rate}/hour lower</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tier cards: exact rate + exact % vs baseline */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <TierCard testid="tier-card-baseline" title="Before MSP" size="Baseline" rate={BASELINE}
          caption="Pre-MSP rate" accent="#605E5C" />
        {TIERS.map((t, i) => (
          <TierCard key={t.key} testid={`tier-card-${t.key}`} title={t.name} size={`${t.sizeLabel} members`}
            rate={t.rate} accent="#0078D4"
            caption={`${exactSavings(t.rate)}% vs $${BASELINE} baseline`}
            step={i > 0 ? `${stepSavings(t.rate, TIERS[i - 1].rate)}% vs ${TIERS[i - 1].name.toLowerCase()} rate` : null} />
        ))}
      </div>

      {/* Savings chart — replicates the source deck (5% / 9% / 20% labels) */}
      <div className="rounded-xl p-5" style={{ background: "#0095DA" }} data-testid="savings-chart">
        <div className="flex items-center gap-2 mb-2">
          <TrendingDown size={18} className="text-white" />
          <h2 className="text-base font-bold tracking-wide text-white uppercase">Savings Chart</h2>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 28, right: 24, left: 0, bottom: 8 }}>
            <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.25)" />
            <XAxis dataKey="tier" interval={0} padding={{ left: 40, right: 40 }} tick={{ fill: "#fff", fontSize: 12 }} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.5)" }} />
            <YAxis domain={[0, 25]} ticks={[0, 5, 10, 15, 20, 25]} tickFormatter={(v) => `${v}%`} width={46}
              tick={{ fill: "#fff", fontSize: 12 }} tickLine={false} axisLine={false} />
            <Tooltip formatter={(v) => [`${v}%`, "Savings"]} contentStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="pct" stroke="#ffffff" strokeWidth={3}
              dot={{ r: 5, fill: "#fff", stroke: "#0095DA", strokeWidth: 2 }}
              label={{ position: "top", fill: "#fff", fontSize: 14, fontWeight: 700, formatter: (v) => `${v}%` }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Reconciliation note */}
      <div className="rounded border border-[#C7E0F4] bg-[#EFF6FC] px-4 py-3 text-xs text-[#004578] flex gap-2" data-testid="savings-note">
        <Info size={14} className="shrink-0 mt-0.5" />
        <span>
          Tier cards show the <b>exact</b> blended rate and the <b>exact</b> cumulative savings versus the pre-MSP
          <b> ${BASELINE}/hour</b> baseline: ${TIERS[0].rate} = {exactSavings(TIERS[0].rate)}%, ${TIERS[1].rate} = {exactSavings(TIERS[1].rate)}%,
          ${TIERS[2].rate} = {exactSavings(TIERS[2].rate)}%. The chart mirrors the source deck's rounded labels (5% cumulative,
          9% step-vs-current, 20% overall) for visual continuity.
        </span>
      </div>
    </div>
  );
}

function TierCard({ testid, title, size, rate, caption, step, accent }) {
  return (
    <div className="fluent-card p-4" data-testid={testid}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#605E5C]">{title}</div>
      <div className="text-[11px] text-[#8A8886]">{size}</div>
      <div className="mt-1.5 text-2xl font-semibold" style={{ color: accent }}>${rate}<span className="text-xs font-normal text-[#605E5C]">/hr</span></div>
      <div className="mt-1 text-[11px] font-semibold text-[#107C41]">{caption}</div>
      {step && <div className="text-[10px] text-[#605E5C]">{step}</div>}
    </div>
  );
}
